/*
  Easify product options - everything the app settings cannot do.

  Three jobs, in the order they have to happen:

  1. Dropdowns render as buttons. Easify leaves its whole option set on
     the page as window.TPOConfigs.option_sets_searchable, and its app
     script is type="module", so it runs deferred - after every classic
     script. That gap is enough to change the option type before the app
     reads it, so the app renders real Buttons itself rather than a
     dropdown this file would then have to fight.

  2. Prices go on the pill as a data-po-price attribute, which the CSS
     prints through ::after - "3 (£18.00)". The app is not printing them
     even with show_price_position on, and the Buttons component keeps
     its text as a bare node with no span to extend, so an attribute is
     the one thing that survives a React re-render without adding a
     child the app would reconcile away.

     The amounts come from the same config, matched through
     data-option-id, which is the option's own id, so a repeated title
     like Hinges cannot pick up another row's price.

  3. The chosen pill gets .is-po-selected. The app does mark it, but
     through a hashed class that changes every release, so there is
     nothing stable to write CSS against. What is stable is that the
     chosen pill is the only one in its group styled differently: its
     class and inline style differ from every sibling. So the odd one
     out is the selection, whatever the app calls it this week.

  4. Swatches follow Theme settings > Swatches, the same as the variant
     picker. The section prints those settings as window.poSwatches, and
     only when swatches are enabled. An option whose title is in "Option
     title" gets data-po-swatch on each pill, a handle of its label, and
     one generated stylesheet colours the ::before dot per option id and
     label. Same reason as the price: an attribute survives the
     re-render where a child span would not.

     The colour is found the way the section finds it: the full label in
     "Custom swatch colours", then a colour word in the label, then the
     fallback. One step sits between the first two, because app labels
     are long: a listed name found inside the label counts, so
     "RAL 9005: #0e0e10" covers "Special Colour RAL 9005 (10-14 days)".
*/
(function () {
  // Types that become Buttons. A checkbox only qualifies when the option
  // is single-select: as a checkbox the app lets a second value through
  // and then complains, where Buttons simply move the choice across.
  var FROM = ['dropdown', 'checkbox'];
  var TO = 'buttons';
  var CLASS = 'is-po-selected';
  var ATTR = 'data-po-price';
  var CURRENCY = '£';
  var SWATCH = 'data-po-swatch';

  // The colour words the section recognises, kept in the same order.
  var CSS_COLOURS = 'black,white,silver,gray,grey,red,blue,green,yellow,orange,brown,gold,beige,ivory,pink,purple,navy,teal,tan,olive,maroon,aqua,lime,cyan,magenta,khaki,plum,salmon,indigo,violet,coral,crimson,turquoise,wheat,linen,snow,azure,orchid,sienna,tomato,chocolate'.split(',');

  // Empty = every dropdown. Add titles to convert only some.
  var ONLY = [];

  // One entry per kind of chooser, each looked at group by group. A
  // checkbox is left out on purpose: it has a real input, so :checked
  // drives it in CSS and a class from here would only fight the toggle.
  var GROUPS = [
    { item: '.po-imageSwatches-optionItem', box: '.po-imageSwatches-swatchesInline' },
    { item: '.po-buttonOption-optionItem', box: '[data-option-id]' },
    { item: '.po-radioOption-option', box: '[data-option-id]' }
  ];

  var PILLS = '.po-buttonOption-optionItem,.po-checkboxOption-option,.po-radioOption-option';

  // option id -> { label: price } for values that cost extra
  var prices = {};

  // option id -> { title, labels } for every option, swatch or not
  var options = {};

  // option ids whose swatch rules are already in the stylesheet
  var painted = {};
  var sheet = null;

  /* "<option id>|<selector>" -> index the shopper clicked. Keyed by id
     rather than by node, because a re-render replaces the nodes. */
  var clicked = {};

  var queued = false;

  /* ---------------------------------------------------------------
     1 + 2. Rewrite the config before the app boots
     --------------------------------------------------------------- */

  function readOption(option) {
    if (!option || !option.id) return;

    var values = option.option_types || [];
    var map = null;

    for (var i = 0; i < values.length; i++) {
      var amount = Number(values[i].additional_price);
      if (!amount) continue;

      if (!map) map = {};
      map[String(values[i].label).trim().toLowerCase()] = amount;
    }

    if (map) prices[option.id] = map;

    options[option.id] = {
      title: String(option.title || ''),
      labels: values.map(function (value) {
        return String(value.label);
      })
    };

    // "multi-select, maximum 1" is single choice in everything but name,
    // and it is the shape that lets a second value through before the app
    // complains. Buttons just move the choice across instead.
    var single = !option.multi_select || Number(option.max_value) === 1;

    // A checkbox with one value is a consent tick, not a choice group, so
    // it stays a checkbox.
    var isGroup = option.type !== 'checkbox' || values.length > 1;

    var convertible = FROM.indexOf(option.type) !== -1 &&
      single &&
      isGroup &&
      (!ONLY.length || ONLY.indexOf(option.title) !== -1);

    if (convertible) {
      option.type = TO;
      option.multi_select = 0;
    }

    // "Selected: RAL 9005" next to the label, where the option asks for it.
    if (option.show_selected_values) option.show_selected_on_label = 1;
  }

  function convert(list) {
    if (!Array.isArray(list)) return list;

    list.forEach(function (entry) {
      var options = entry && entry.data && entry.data.options;
      if (Array.isArray(options)) options.forEach(readOption);
    });

    return list;
  }

  /*
    The config may or may not be on the page yet, depending on where this
    script sits relative to the app embed, so handle both: convert what
    is already there, otherwise convert on the way in.
  */
  function watch(configs) {
    if (!configs) return false;

    if (configs.option_sets_searchable) {
      convert(configs.option_sets_searchable);
      return true;
    }

    var stored;
    Object.defineProperty(configs, 'option_sets_searchable', {
      configurable: true,
      get: function () {
        return stored;
      },
      set: function (value) {
        stored = convert(value);
      }
    });

    return true;
  }

  if (!watch(window.TPOConfigs)) {
    var pending;
    Object.defineProperty(window, 'TPOConfigs', {
      configurable: true,
      get: function () {
        return pending;
      },
      set: function (value) {
        pending = value;
        watch(value);
      }
    });
  }

  /* ---------------------------------------------------------------
     Painting, after every render
     --------------------------------------------------------------- */

  function money(amount) {
    return CURRENCY + amount.toFixed(2);
  }

  function pricePills(root, id) {
    var map = prices[id];
    if (!map) return;

    var pills = root.querySelectorAll(PILLS);

    for (var i = 0; i < pills.length; i++) {
      var pill = pills[i];

      // ::after content is not part of textContent, so this stays the
      // plain label however many times it runs.
      var amount = map[pill.textContent.trim().toLowerCase()];
      var want = amount ? ' (' + money(amount) + ')' : null;

      if (!want) {
        if (pill.hasAttribute(ATTR)) pill.removeAttribute(ATTR);
        continue;
      }

      if (pill.getAttribute(ATTR) !== want) pill.setAttribute(ATTR, want);
    }
  }

  /* ---------------------------------------------------------------
     4. Swatches, from Theme settings
     --------------------------------------------------------------- */

  function handle(text) {
    return String(text).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  // "Select Finish / Colour" -> ['select finish', 'colour'], as the section splits it.
  function swatchNames(settings) {
    return String(settings.names || '').toLowerCase().split(/[\/,]/).map(function (name) {
      return name.trim();
    });
  }

  // "blue: #1F4E79" per line -> [['blue', '#1F4E79']]
  function swatchMap(settings) {
    return String(settings.map || '').split(/\r?\n/).reduce(function (list, line) {
      var parts = line.split(':');
      if (parts.length > 1 && parts[0].trim()) {
        list.push([parts[0].trim().toLowerCase(), parts[1].trim()]);
      }
      return list;
    }, []);
  }

  function lookup(map, key) {
    for (var i = 0; i < map.length; i++) {
      if (map[i][0] === key) return map[i][1];
    }
    return null;
  }

  function colourFor(label, map, fallback) {
    var key = label.trim().toLowerCase();

    // 1. The full label
    var found = lookup(map, key);
    if (found) return found;

    // 2. A listed name inside the label, the longest one winning
    var best = '';
    for (var i = 0; i < map.length; i++) {
      var name = map[i][0];
      var escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      if (name.length > best.length && new RegExp('(^|[^a-z0-9])' + escaped + '($|[^a-z0-9])').test(key)) {
        best = name;
        found = map[i][1];
      }
    }
    if (found) return found;

    // 3. A colour word, mapped if a custom shade exists. The last word wins, as in the section.
    var detected = null;
    key.replace(/-/g, ' ').split(' ').forEach(function (word) {
      if (CSS_COLOURS.indexOf(word) !== -1) detected = word;
    });
    if (detected) return lookup(map, detected) || detected;

    // 4. Fallback
    return fallback;
  }

  function isSwatchOption(id, names) {
    var option = options[id];
    return !!option && names.indexOf(option.title.trim().toLowerCase()) !== -1;
  }

  function swatchRules(id, map, fallback) {
    var rules = '';

    options[id].labels.forEach(function (label) {
      var colour = colourFor(label, map, fallback).replace(/[;{}<>]/g, '');
      rules += '.easify-product-options [data-option-id="' + id + '"] [' + SWATCH + '="' + handle(label) + '"]::before{background-color:' + colour + ' !important}\n';
    });

    return rules;
  }

  /*
    The pill's text can carry more than its label - the hidden tooltip
    repeats it - so the pill is matched to the config label it contains,
    the longest one winning, rather than read as-is.
  */
  function labelFor(id, pill) {
    // No word boundary: a repeated label runs straight into itself.
    var text = handle(pill.textContent);
    var best = '';

    options[id].labels.forEach(function (label) {
      var key = handle(label);
      if (key.length > best.length && text.indexOf(key) !== -1) best = key;
    });

    return best || null;
  }

  function swatchPills(root) {
    var settings = window.poSwatches;
    if (!settings) return;

    var id = root.getAttribute('data-option-id');
    var on = isSwatchOption(id, swatchNames(settings));
    var pills = root.querySelectorAll(PILLS);

    if (on && !painted[id]) {
      if (!sheet) {
        sheet = document.createElement('style');
        sheet.id = 'po-swatches';
        document.head.appendChild(sheet);
      }

      sheet.textContent += swatchRules(id, swatchMap(settings), settings.fallback || '#D9DDE2');
      painted[id] = true;
    }

    for (var i = 0; i < pills.length; i++) {
      var want = on ? labelFor(id, pills[i]) : null;

      if (!want) {
        if (pills[i].hasAttribute(SWATCH)) pills[i].removeAttribute(SWATCH);
        continue;
      }

      if (pills[i].getAttribute(SWATCH) !== want) pills[i].setAttribute(SWATCH, want);
    }
  }

  /*
    What the app styles differently is what the app has selected, and the
    only reliable trace of that is the markup: a class, an attribute, an
    inline style, or a marker on a child. Computed colour is no use here,
    because this file's own CSS paints every pill the same and so erases
    the very difference we would be looking for.

    Our own additions are stripped first so the comparison sees the app's
    markup, not the last thing we did to it.
  */
  function signature(item) {
    var parts = [];
    var attrs = item.attributes;
    var i;

    for (i = 0; i < attrs.length; i++) {
      var name = attrs[i].name;
      if (name === ATTR || name === SWATCH) continue;

      var value = attrs[i].value;
      if (name === 'class') {
        value = value.replace(new RegExp('\\b' + CLASS + '\\b', 'g'), '');
      }

      parts.push(name + '=' + value.trim().replace(/\s+/g, ' '));
    }

    // A marker one level down counts too.
    var kids = item.querySelectorAll('[class],[style]');
    for (i = 0; i < kids.length; i++) {
      parts.push((kids[i].getAttribute('class') || '') + '|' + (kids[i].getAttribute('style') || ''));
    }

    return parts.join('~');
  }

  function groupKey(box, selector) {
    var owner = box.closest('[data-option-id]');
    if (!owner) return null;

    return owner.getAttribute('data-option-id') + '|' + selector;
  }

  function markOddOne(box, selector) {
    var items = box.querySelectorAll(selector);
    if (items.length < 2) return;

    /*
      A click is the one signal that needs no guessing, so once the
      shopper has chosen in this group it outranks the comparison below.
    */
    var key = groupKey(box, selector);
    if (key && clicked[key] !== undefined) {
      for (var p = 0; p < items.length; p++) {
        items[p].classList.toggle(CLASS, p === clicked[key]);
      }
      return;
    }

    var signatures = [];
    var counts = {};
    var common = '';
    var best = 0;
    var i;

    for (i = 0; i < items.length; i++) {
      var sig = signature(items[i]);
      signatures.push(sig);
      counts[sig] = (counts[sig] || 0) + 1;

      if (counts[sig] > best) {
        best = counts[sig];
        common = sig;
      }
    }

    // Every sibling identical means nothing is selected yet.
    if (best === items.length) {
      for (i = 0; i < items.length; i++) items[i].classList.remove(CLASS);
      return;
    }

    for (i = 0; i < items.length; i++) {
      // toggle only writes when the value changes, so this cannot bounce
      // against the observer below.
      items[i].classList.toggle(CLASS, signatures[i] !== common);
    }
  }

  function paint() {
    queued = false;

    var roots = document.querySelectorAll('.easify-product-options [data-option-id]');

    for (var i = 0; i < roots.length; i++) {
      pricePills(roots[i], roots[i].getAttribute('data-option-id'));
      swatchPills(roots[i]);

      for (var g = 0; g < GROUPS.length; g++) {
        var boxes = GROUPS[g].box === '[data-option-id]'
          ? [roots[i]]
          : roots[i].querySelectorAll(GROUPS[g].box);

        for (var b = 0; b < boxes.length; b++) {
          markOddOne(boxes[b], GROUPS[g].item);
        }
      }
    }
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(paint);
  }

  /*
    A click is recorded straight away: the app re-renders right after, and
    paint() then keeps the class on the same pill through every render.
  */
  document.addEventListener('click', function (event) {
    for (var g = 0; g < GROUPS.length; g++) {
      var item = event.target.closest(GROUPS[g].item);
      if (!item) continue;

      var box = GROUPS[g].box === '[data-option-id]'
        ? item.closest('[data-option-id]')
        : item.closest(GROUPS[g].box);

      if (box) {
        var key = groupKey(box, GROUPS[g].item);
        var siblings = box.querySelectorAll(GROUPS[g].item);

        if (key) {
          var index = Array.prototype.indexOf.call(siblings, item);

          // Clicking the active pill again clears the group. -1 matches
          // no pill, and it is still a recorded choice, so the comparison
          // in markOddOne does not step in and re-select it.
          clicked[key] = clicked[key] === index ? -1 : index;
        }
      }

      paint();
      return;
    }
  });

  /*
    The app mounts after the theme and re-renders on every choice, so the
    whole page is watched rather than one container that may not exist
    yet. Watching class and style is also what feeds the comparison
    above: a click reaches us as the app's own re-render.
  */
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style']
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
